/**
 * risk/positionSizing.ts
 *
 * Advisory position sizing engine for MER Algo Footprints v4.3+
 *
 * This is NOT a trade execution system.
 * It is a pre-trade sizing intelligence layer that suggests how aggressively
 * to size a setup given its quality, portfolio context, and risk model.
 *
 * Sizing tiers:
 *   avoid    — do not trade (too risky / conflicted / low quality)
 *   probe    — very small position (confirm thesis before committing)
 *   standard — normal allocation for this quality level
 *   conviction — larger allocation (high quality + portfolio aligned)
 *
 * When no account settings are provided, all outputs are normalized
 * as fractions/multiples of an assumed unit risk — this is made explicit.
 *
 * DISCLOSURE: Portfolio logic is based on the opportunity board heuristics,
 * not real position holdings. Sizing suggestions are advisory only.
 */

import { ReadinessTier } from '../models/riskEngine';
import { PositionEntry } from '../models/portfolioRisk';
import { PortfolioHeat } from '../models/portfolioRisk';

// ─── Risk model settings ──────────────────────────────────────────────────────
// Stored in AppSettings — these are the user-configurable fields

export interface RiskModelSettings {
  accountSize:         number | null;   // null = not configured (normalized mode)
  riskPerTradePct:     number;          // % of account to risk per trade (default 1%)
  maxConcurrentTrades: number;          // max simultaneous open positions (default 5)
  maxSameThemePct:     number;          // max % of risk in one theme (default 30%)
  maxSameDirectionPct: number;          // max % of risk in one direction (default 60%)
}

export const DEFAULT_RISK_MODEL: RiskModelSettings = {
  accountSize:         null,
  riskPerTradePct:     1.0,
  maxConcurrentTrades: 5,
  maxSameThemePct:     30,
  maxSameDirectionPct: 60,
};

// ─── Sizing tier ─────────────────────────────────────────────────────────────

export type SizingTier = 'avoid' | 'probe' | 'standard' | 'conviction';

export const SIZING_TIER_LABELS: Record<SizingTier, string> = {
  avoid:      '✕ AVOID',
  probe:      '◎ PROBE',
  standard:   '● STANDARD',
  conviction: '⚡ CONVICTION',
};

export const SIZING_TIER_COLORS: Record<SizingTier, string> = {
  avoid:      'var(--red)',
  probe:      'var(--blue-dim)',
  standard:   'var(--green)',
  conviction: 'var(--gold)',
};

export const SIZING_TIER_MULTIPLIERS: Record<SizingTier, number> = {
  avoid:      0,
  probe:      0.33,
  standard:   1.0,
  conviction: 1.5,
};

// ─── Sizing output ────────────────────────────────────────────────────────────

export interface SizingAssessment {
  // Tier recommendation
  tier:            SizingTier;
  tierReason:      string;          // plain-English why this tier

  // Risk per trade inputs
  riskPerShare:    number | null;   // $ risk per share (null if levels missing)
  rewardPerShare:  number | null;
  expectedR:       number | null;

  // Dollar-based suggestions (null if no account size configured)
  dollarRiskTarget:    number | null;  // $ to risk on this trade
  suggestedShares:     number | null;  // shares to trade for that dollar risk
  maxPositionValue:    number | null;  // notional value at that share count

  // Normalized (always available — as fraction of standard unit risk)
  normalizedRiskUnits: number;         // e.g. 0.5 = half a standard unit

  // Constraint notes
  constraintNotes: string[];           // warnings that reduced the tier
  disclosureNote:  string;             // standard advisory disclosure
}

// ─── Main sizing function ─────────────────────────────────────────────────────

export function assessPositionSize(
  entryPrice:     number | null,
  stopPrice:      number | null,
  target1Price:   number | null,
  readiness:      ReadinessTier,
  readinessScore: number,          // 0–100
  setupConfidence: number,         // 0–100
  portfolioHeat:  PortfolioHeat,
  concentrationScore: number,      // 0–100
  conflicts:      number,          // conflict count
  redundancies:   number,          // redundancy count
  riskModel:      RiskModelSettings = DEFAULT_RISK_MODEL,
  existingPosition?: PositionEntry | null  // optional — adjusts sizing if already in symbol
): SizingAssessment {
  // If already in this symbol, start from a probe tier regardless of readiness
  // (avoid adding to a position without explicit decision)
  const positionNote = existingPosition
    ? `Existing ${existingPosition.dir} position (${existingPosition.size} shares @ $${existingPosition.entryPrice})`
    : null;
  const constraintNotes: string[] = [];

  // ── Per-share risk ────────────────────────────────────────────────────────
  let riskPerShare:   number | null = null;
  let rewardPerShare: number | null = null;
  let expectedR:      number | null = null;

  if (entryPrice !== null && stopPrice !== null) {
    riskPerShare = Math.abs(entryPrice - stopPrice);
    if (target1Price !== null) {
      rewardPerShare = Math.abs(target1Price - entryPrice);
      expectedR = riskPerShare > 0 ? rewardPerShare / riskPerShare : null;
    }
  }

  // ── Base tier from readiness ──────────────────────────────────────────────
  let tier: SizingTier;
  let tierReason: string;

  if (readiness === 'urgent' && readinessScore >= 80) {
    tier = 'conviction';
    tierReason = 'High-readiness urgent setup — full conviction sizing';
  } else if (readiness === 'ready') {
    tier = 'standard';
    tierReason = 'Ready setup — standard risk allocation';
  } else if (readiness === 'good') {
    tier = 'probe';
    tierReason = 'Good-quality setup developing — probe to confirm';
  } else {
    tier = 'avoid';
    tierReason = 'Interesting but not ready — wait for confirmation';
  }

  // Exhaustiveness: adding a new SizingTier value will cause a compile error here.
  const _sizingCheck: Record<SizingTier, true> = {
    avoid: true, probe: true, standard: true, conviction: true,
  };
  void _sizingCheck[tier];

  // ── Portfolio heat adjustments ────────────────────────────────────────────
  if (portfolioHeat === 'overloaded') {
    tier = downgrade(tier);
    constraintNotes.push('Portfolio overloaded — reducing size');
  } else if (portfolioHeat === 'crowded') {
    if (tier === 'conviction') { tier = 'standard'; constraintNotes.push('Crowded board — capping at standard'); }
  }

  // ── Concentration adjustments ─────────────────────────────────────────────
  if (concentrationScore >= 60) {
    tier = downgrade(tier);
    constraintNotes.push(`High sector concentration (${concentrationScore}) — reducing size`);
  }

  // ── Conflict adjustments ──────────────────────────────────────────────────
  if (conflicts > 0) {
    tier = downgrade(tier);
    constraintNotes.push(`${conflicts} portfolio conflict(s) — reducing size`);
  }

  // ── Redundancy adjustments ────────────────────────────────────────────────
  if (redundancies > 0 && tier === 'conviction') {
    tier = 'standard';
    constraintNotes.push(`${redundancies} redundant trade(s) on board — capping at standard`);
  }

  // ── Existing position check ──────────────────────────────────────────────
  if (existingPosition) {
    // Already in this symbol — cap at standard to avoid unintended pyramid
    if (tier === 'conviction') {
      tier = 'standard';
      constraintNotes.push(`Position already open (${existingPosition.size}sh) — capping at standard`);
    }
  }

  // ── Poor R adjustments ────────────────────────────────────────────────────
  if (expectedR !== null && expectedR < 1.5) {
    if (tier === 'conviction') { tier = 'standard'; constraintNotes.push('R/R < 1.5 — capping at standard'); }
    if (tier === 'standard' && expectedR < 1.0) { tier = 'probe'; constraintNotes.push('R/R < 1.0 — reducing to probe'); }
  }

  // ── Low confidence ────────────────────────────────────────────────────────
  if (setupConfidence < 45 && tier === 'conviction') {
    tier = 'standard';
    constraintNotes.push('Below-average confidence — capping at standard');
  }

  // ── Dollar calculations ───────────────────────────────────────────────────
  const multiplier = SIZING_TIER_MULTIPLIERS[tier];
  const normalizedRiskUnits = multiplier;

  let dollarRiskTarget:  number | null = null;
  let suggestedShares:   number | null = null;
  let maxPositionValue:  number | null = null;

  if (riskModel.accountSize !== null && riskModel.accountSize > 0) {
    const baseRisk = (riskModel.accountSize * riskModel.riskPerTradePct) / 100;
    dollarRiskTarget = +(baseRisk * multiplier).toFixed(2);
    if (riskPerShare !== null && riskPerShare > 0 && dollarRiskTarget > 0) {
      suggestedShares  = Math.floor(dollarRiskTarget / riskPerShare);
      maxPositionValue = entryPrice !== null ? +(suggestedShares * entryPrice).toFixed(2) : null;
    }
  }

  // Include position note in constraints if present
  if (positionNote) constraintNotes.unshift(positionNote);

  const disclosureNote = riskModel.accountSize === null
    ? 'No account size configured — sizing shown as normalized risk units. Configure account size in Settings → Risk Model for dollar amounts.'
    : 'Sizing is advisory only. Based on opportunity board heuristics, not actual position holdings. Always verify against your actual exposure.';

  return {
    tier, tierReason,
    riskPerShare:     riskPerShare    !== null ? +riskPerShare.toFixed(4)    : null,
    rewardPerShare:   rewardPerShare  !== null ? +rewardPerShare.toFixed(4)  : null,
    expectedR:        expectedR       !== null ? +expectedR.toFixed(2)       : null,
    dollarRiskTarget,
    suggestedShares,
    maxPositionValue,
    normalizedRiskUnits,
    constraintNotes,
    disclosureNote,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function downgrade(tier: SizingTier): SizingTier {
  if (tier === 'conviction') return 'standard';
  if (tier === 'standard')   return 'probe';
  return 'avoid';
}
